import { useCoreService } from 'core/services/useCoreService';
import { createContext, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AccountsTotalsData } from '../models/AccountsTotalsData';
import {
  SEARCH_BUSINESS_TOTALS_BY_PERIOD_PATH,
  SEARCH_BUSINESS_TOTALS_PATH,
} from 'core/empresa/routes';
import { isInvalidPeriod } from 'shared/validations/ValidatePeriodo';
import { useDataFetcher } from 'shared/http';
import { LoadingSpinner } from 'shared/spinner/components/LoadingSpinner';
import { useNotifications } from 'shared/notifications';

interface IAccountsTotalsContext {
  goToSearch(): void;
  accountsTotalsData: AccountsTotalsData;
  searchParams: URLSearchParams;
  liquidationInProcess: boolean;
}

export const AccountsTotalsContext = createContext<IAccountsTotalsContext>({
  goToSearch: () => {},
  accountsTotalsData: null,
  searchParams: null,
  liquidationInProcess: false,
});

export const AccountsTotalsProvider = ({ children }) => {
  const [accountsTotalsData, setAccountsTotalsDataRequest] =
    useDataFetcher<AccountsTotalsData>(null);
  const [searchParams] = useSearchParams();
  const { addNotification } = useNotifications();
  const { state } = useLocation();
  const coreService = useCoreService();
  const navigate = useNavigate();

  useEffect(() => {
    let period;
    if (searchParams.has('mes') && searchParams.has('anio')) {
      period = { mes: searchParams.get('mes'), anio: searchParams.get('anio') };
    }
    if (period != null && isInvalidPeriod(period)) {
      addNotification('No puede ver movimientos más antiguos a un año', 'error');
      navigate(SEARCH_BUSINESS_TOTALS_BY_PERIOD_PATH);
    }
    setAccountsTotalsDataRequest(() => coreService.searchAccountsTotalsReport(period));
  }, [
    coreService,
    addNotification,
    navigate,
    searchParams,
    setAccountsTotalsDataRequest,
  ]);

  const contextValue = {
    goToSearch: () => {
      if (state && state['fromPath']) {
        navigate(
          {
            pathname: state['fromPath'],
            search: searchParams.toString(),
          },
          {
            state,
          },
        );
      } else if (searchParams.has('mes') && searchParams.has('anio')) {
        navigate(SEARCH_BUSINESS_TOTALS_BY_PERIOD_PATH);
      } else {
        navigate(SEARCH_BUSINESS_TOTALS_PATH);
      }
    },
    accountsTotalsData: accountsTotalsData.data,
    searchParams,
    liquidationInProcess: false,
  };
  return (
    <AccountsTotalsContext.Provider value={contextValue}>
      <LoadingSpinner isLoading={accountsTotalsData.isLoading}>{children}</LoadingSpinner>
    </AccountsTotalsContext.Provider>
  );
};
